import { useEffect, useMemo, useRef } from 'react';
import { Lead, LeadStatus } from '../types';
import { leadsService, QuotaCheckResult } from '../services/supabase';
import { useInfiniteData } from './useInfiniteData';
import {
  useOptimisticCRUD,
  createOptimisticItem,
  OptimisticQuotaBlocked,
} from './useOptimisticCRUD';
import { useQuota } from '../context/QuotaContext';
import { LEAD_UPDATED_EVENT } from '../utils/leadEvents';

interface UseInfiniteLeadsOptions {
  limit?: number;
  // Server-side filters (avoid loading all leads)
  filters?: Partial<{
    term: string;
    status: LeadStatus | '';
    sector: string;
    contactType: string;
    direction: 'inbound' | 'outbound' | '';
  }>;
  /** Optional: called when an update fails before rollback (e.g. show toast) */
  onUpdateFailed?: () => void;
}

interface LeadCRUD {
  create: (data: Omit<Lead, 'id' | 'createdAt' | 'updatedAt'>) => void;
  update: (id: string, data: Lead, options?: { previousItem?: Lead }) => void;
  remove: (id: string) => void;
  bulkRemove: (ids: string[]) => void;
  bulkUpdate: (leads: Lead[]) => void;
}

interface UseInfiniteLeadsReturn {
  leads: Lead[];
  total?: number;
  loadingInitial: boolean;
  loadingMore: boolean;
  refreshing: boolean;
  error: string | null;
  hasMore: boolean;
  loadMore: () => Promise<void>;
  refresh: () => Promise<void>;
  reset: () => void;
  crud: LeadCRUD;
  // low-level
  addLead: (lead: Lead) => void;
}

export const useInfiniteLeads = (options: UseInfiniteLeadsOptions = {}): UseInfiniteLeadsReturn => {
  const { limit = 30, filters, onUpdateFailed } = options;
  const { showQuotaLimit } = useQuota();
  const listActionsRef = useRef({
    removeItem: (_id: string) => {},
    replaceItem: (_tempId: string, _item: Lead) => {},
    updateItem: (_item: Lead) => {},
  });

  const filtersKey = useMemo(() => {
    if (!filters) return '';
    const term = (filters.term || '').trim();
    const status = (filters.status || '').trim();
    const sector = (filters.sector || '').trim();
    const contactType = (filters.contactType || '').trim();
    const direction = (filters.direction || '').trim();
    if (!term && !status && !sector && !contactType && !direction) return '';
    return `term=${encodeURIComponent(term)}&status=${encodeURIComponent(status)}&sector=${encodeURIComponent(sector)}&contactType=${encodeURIComponent(contactType)}&direction=${encodeURIComponent(direction)}`;
  }, [filters]);

  const {
    items,
    total,
    loadingInitial,
    loadingMore,
    refreshing,
    error,
    hasMore,
    loadMore,
    refresh,
    replaceWithFirstPage,
    reset,
    addItem,
    updateItem,
    removeItem,
    replaceItem,
  } = useInfiniteData<Lead>({
    limit,
    cacheKey: filtersKey ? `leads:filtered:${filtersKey}` : 'leads',
    service: {
      getCursorPage: async (l, cursor) => {
        const result = filtersKey
          ? await leadsService.getCursorPageFiltered(l, cursor as any, {
              term: filters?.term || '',
              status: String(filters?.status || ''),
              sector: String(filters?.sector || ''),
              contactType: String(filters?.contactType || ''),
              direction: String(filters?.direction || ''),
            })
          : await leadsService.getCursorPage(l, cursor as any);
        return {
          items: result.leads,
          total: result.total,
          nextCursor: result.nextCursor,
          hasMore: result.hasMore,
        };
      },
    },
  });

  // Sort by last_action_at desc so any update (notes, status, etc.) makes the lead move up
  const leads = useMemo(
    () =>
      [...items].sort((a, b) => {
        const ta = a.lastActionAt ? new Date(a.lastActionAt).getTime() : -1;
        const tb = b.lastActionAt ? new Date(b.lastActionAt).getTime() : -1;
        if (ta !== tb) return tb - ta;
        const ua = a.updatedAt ? new Date(a.updatedAt).getTime() : 0;
        const ub = b.updatedAt ? new Date(b.updatedAt).getTime() : 0;
        if (ua !== ub) return ub - ua;
        return (
          (b.createdAt ? new Date(b.createdAt).getTime() : 0) -
          (a.createdAt ? new Date(a.createdAt).getTime() : 0)
        );
      }),
    [items]
  );

  // Filtres : replace sans reset (stale-while-revalidate) pour éviter le retour en haut au tap.
  // On ne déclenche replaceWithFirstPage que lorsque filtersKey a vraiment changé (pas au 1er run).
  const prevFiltersKeyRef = useRef<string | undefined>(undefined);
  useEffect(() => {
    if (!filters) return;
    const prev = prevFiltersKeyRef.current;
    prevFiltersKeyRef.current = filtersKey;
    if (prev === undefined) return; // 1er run : chargement initial par useInfiniteData
    if (prev === filtersKey) return; // même filtre, rien à faire
    void replaceWithFirstPage();
  }, [filtersKey]); // eslint-disable-line react-hooks/exhaustive-deps

  listActionsRef.current = { removeItem, replaceItem, updateItem };

  const optimisticCRUD = useOptimisticCRUD<
    Lead,
    Omit<Lead, 'id' | 'createdAt' | 'updatedAt'>,
    QuotaCheckResult
  >({
    onOptimisticAdd: addItem,
    onOptimisticUpdate: updateItem,
    onOptimisticRemove: removeItem,
    onOptimisticReplace: replaceItem,
    service: {
      create: async (data) => {
        const result = await leadsService.create(data) as any;
        if (result?.error === 'QUOTA_LIMIT') {
          return {
            __quotaBlocked: true,
            quotaInfo: result.quotaInfo as QuotaCheckResult,
          } satisfies OptimisticQuotaBlocked<QuotaCheckResult>;
        }
        return result && !result.error ? (result as Lead) : null;
      },
      update: async (id, data) => {
        return await leadsService.update(id, data as Lead);
      },
      delete: async (id) => {
        return await leadsService.delete(id);
      },
    },
    onQuotaBlocked: ({ tempId, data, quotaInfo }) => {
      showQuotaLimit(quotaInfo, {
        onDismiss: () => {
          listActionsRef.current.removeItem(tempId);
        },
        onSlotFreed: async () => {
          const created = await leadsService.create(data) as any;
          if (created && !created.error && created.id) {
            listActionsRef.current.replaceItem(tempId, created as Lead);
            return;
          }
          listActionsRef.current.removeItem(tempId);
        },
      });
      return true;
    },
    onRollback: refresh,
    onUpdateFailed,
  });

  useEffect(() => {
    const onExternalUpdate = (event: Event) => {
      const lead = (event as CustomEvent<Lead>).detail;
      if (!lead?.id) return;
      listActionsRef.current.updateItem(lead);
    };
    window.addEventListener(LEAD_UPDATED_EVENT, onExternalUpdate);
    return () => window.removeEventListener(LEAD_UPDATED_EVENT, onExternalUpdate);
  }, []);

  const crud: LeadCRUD = useMemo(
    () => ({
      create: (data) => {
        const optimisticLead = createOptimisticItem<Lead>(data);
        // Always add optimistically for instant feedback
        optimisticCRUD.create(data, optimisticLead);
      },
      update: (id, data, options) => {
        optimisticCRUD.update(id, { ...data, updatedAt: new Date() }, options);
      },
      remove: (id) => optimisticCRUD.remove(id),
      bulkRemove: (ids) => optimisticCRUD.bulkRemove(ids),
      bulkUpdate: (items) => {
        const updated = items.map((l) => ({ ...l, updatedAt: new Date() }));
        optimisticCRUD.bulkUpdate(updated);
      },
    }),
    [optimisticCRUD]
  );

  return {
    leads,
    total,
    loadingInitial,
    loadingMore,
    refreshing,
    error,
    hasMore,
    loadMore,
    refresh,
    reset,
    crud,
    addLead: addItem,
  };
};

